/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import styles from "./Report.module.css";
import api from "../Api";
import Select from "react-select";
import {
    Grid,
    Typography,
    Box,
    Card,
    CardContent,
    Avatar,
    Chip,
    Paper,
    Tooltip as MuiTooltip
} from "@material-ui/core";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
} from "recharts";
import { startLoader, stopLoader } from "../../../actions/loaderActions";
import { useDispatch } from 'react-redux';
import { TrendingUp, Folder, Category, Assessment } from "@material-ui/icons";
import EmptyChartImage from "../../../common/images/no-report.svg";
import { EmojiEvents } from "@material-ui/icons";
import { Button, Menu, MenuItem } from "@material-ui/core";
import { GetApp as ExportIcon } from "@material-ui/icons";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import { useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { IconButton } from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

const getDefaultHeaders = () => ({
    "x-tenant-schema": "dbo",
    "x-user-id": localStorage.getItem("UserId") || "",
    "x-user-email": localStorage.getItem("userEmail") || "",
});

const Report = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    // Summary counts
    const [summary, setSummary] = useState([]);
    const [topPerformers, setTopPerformers] = useState([]);
    // Row1 filters state
    const [companies, setCompanies] = useState([]);
    const [entities, setEntities] = useState([]);
    const [operatingUnits, setOperatingUnits] = useState([]);
    const [selectedUploadLevel, setSelectedUploadLevel] = useState("company");
    const [selectedCompanyId, setSelectedCompanyId] = useState("");
    const [selectedEntityId, setSelectedEntityId] = useState("");
    const [selectedUnitId, setSelectedUnitId] = useState("");

    // Row2 filters state
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [years, setYears] = useState([{ label: "FY2025", value: "FY2025" }]);
    const [exportType, setExportType] = useState({ label: "excel", value: "excel" });

    // Chart & performers state
    const [chartData, setChartData] = useState([]);
    const [unitLabel, setUnitLabel] = useState("");

    const iconMap = {
        "Files Ingested": <Folder />,
        "Data Categories": <Category />,
        "Data Templates": <Assessment />,
        "Completion for GRI": <TrendingUp />,
    };
    const colorMap = ["#1976d2", "#9c27b0", "#f57c00", "#2e7d32"];
    const rankColors = ["#FFD700", "#C0C0C0", "#CD7F32"];
    const [rawOperatingUnits, setRawOperatingUnits] = useState([]);


    const chartRef = useRef();
    const [anchorEl, setAnchorEl] = useState(null);
    const history = useHistory();
    const userId = localStorage.getItem("UserId");

    // Fetch companies/entities/units once
    useEffect(() => {
        async function fetchMeta() {
            dispatch(startLoader());
            const userEmail = sessionStorage.getItem("userEmail") || localStorage.getItem("userEmail") || "";
            const headers = {
                "x-user-id": localStorage.getItem("UserId") || "",
                "x-tenant-schema": "Amplo111",
                "x-user-email": userEmail,
            };

            const res = await api.get("/getAllCompanies", { headers });
            let comps = res.data.data || [];

            // FILTER by Project Selection Context if available
            if (userEmail && userEmail.toLowerCase() === "superadmin@amplo.com") {
                const projectSelectionStr = sessionStorage.getItem("projectSelection");
                if (projectSelectionStr) {
                    try {
                        const projectSelection = JSON.parse(projectSelectionStr);
                        // Use client.company_id as primary source of truth, similar to NewCompanyProfile
                        if (projectSelection.client && projectSelection.client.company_id) {
                            const contextCompanyId = String(projectSelection.client.company_id);
                            const contextCompany = comps.find(c => String(c.company_id) === contextCompanyId);
                            if (contextCompany) {
                                comps = [contextCompany];
                            }
                        } else if (projectSelection.ampProject && projectSelection.ampProject.CompanyID) {
                            // Fallback to ampProject if client object is missing (older saved state?)
                            const contextCompanyId = String(projectSelection.ampProject.CompanyID);
                            const contextCompany = comps.find(c => String(c.company_id) === contextCompanyId);
                            if (contextCompany) {
                                comps = [contextCompany];
                            }
                        }
                    } catch (e) {
                        console.error("Error parsing project selection:", e);
                    }
                }
            }

            setCompanies(comps);
            if (comps.length > 0) {
                setSelectedCompanyId(comps[0].company_id);
            }

            setEntities(
                comps.flatMap(c =>
                    (c.entity_level_industry_mappings || []).map(e => ({
                        entity_id: e.entity_id,
                        entity_name: e.entity_name,
                        company_id: c.company_id,
                    }))
                )
            );

            setRawOperatingUnits(
                comps.flatMap(c => ({
                    company_id: c.company_id,
                    company_level_operating_units: c.company_level_operating_units || [],
                    entity_level_operating_units: c.entity_level_operating_units || [],
                }))
            );
        }

        fetchMeta();
    }, []);

    useEffect(() => {
        if (!selectedCompanyId) {
            setOperatingUnits([]);
            return;
        }

        const companyData = rawOperatingUnits.find(c => c.company_id === selectedCompanyId);
        if (!companyData) {
            setOperatingUnits([]);
            return;
        }

        let units = [];

        if (selectedEntityId) {
            // Filter only entity-level units
            units = companyData.entity_level_operating_units
                .filter(u => u.entity_id === selectedEntityId)
                .map(u => ({
                    unit_id: u.unit_id,
                    name: u.name,
                    company_id: selectedCompanyId,
                }));
        } else {
            // Filter only company-level units
            units = companyData.company_level_operating_units.map(u => ({
                unit_id: u.unit_id,
                name: u.name,
                company_id: selectedCompanyId,
            }));
        }

        setOperatingUnits(units);
        setSelectedUnitId(""); // Reset selected unit when filters change
    }, [rawOperatingUnits, selectedCompanyId, selectedEntityId]);


    // Fetch templates when filters change
    useEffect(() => {
        async function fetchTemplates() {
            dispatch(startLoader());
            const params = {
                companyId: selectedCompanyId || undefined,
                entityId: selectedEntityId || undefined,
                unitId: selectedUnitId || undefined,
            };

            const res = await api.get("/getDataTemplatesForReporting", {
                headers: getDefaultHeaders(),
                params,
            });

            const list = (res.data.templates || []).map(t => ({
                label: t.name,
                value: t.templateId,
                fileIds: t.fileIds
            }));

            setTemplates(list);
            setSelectedTemplate(null);
            setChartData([]);
            setUnitLabel("");
            dispatch(stopLoader());
        }
        fetchTemplates();
    }, [selectedCompanyId, selectedEntityId, selectedUnitId]);

    // Fetch the actual data-rows for the selected template
    useEffect(() => {
        async function fetchTemplateData() {
            dispatch(startLoader());
            if (!selectedTemplate) {
                setChartData([]);
                setUnitLabel("");
                return;
            }

            // 1) Determine unit label
            const name = selectedTemplate.label.toLowerCase();
            let unit = "";
            if (name.includes("electricity")) unit = "kWh";
            else if (name.includes("diesel")) unit = "L";
            else if (name.includes("lpg")) unit = "kg";
            setUnitLabel(unit);

            // 2) Grab & serialize file IDs
            const fileIds = Array.isArray(selectedTemplate.fileIds)
                ? selectedTemplate.fileIds
                : [];
            const fileidParam = fileIds.length
                ? fileIds.join(",")
                : undefined;

            // 3) Build params
            const params = {
                companyId: selectedCompanyId || undefined,
                entityId: selectedEntityId || undefined,
                unitId: selectedUnitId || undefined,
                fileid: fileidParam,
            };

            try {
                // 4) Fetch data-rows
                const res = await api.get(
                    `/${selectedTemplate.value}/data-rows`,
                    { headers: getDefaultHeaders(), params }
                );
                dispatch(startLoader());
                const rows = Array.isArray(res.data.rows) ? res.data.rows : [];

                if (rows.length === 0) {
                    setChartData([]);
                    dispatch(stopLoader());
                    return;
                }

                const chart = rows.map(r => ({
                    date: new Date(r["Time Period Start Date"]).getTime(),
                    consumption: Number(r.Consumption) || 0,
                }));

                setChartData(chart);
                dispatch(stopLoader());
            } catch (err) {
                console.error("Failed to fetch template data-rows:", err);
                setChartData([]);
                setUnitLabel("");
                dispatch(stopLoader());
            }
        }

        fetchTemplateData();
    }, [
        selectedTemplate,
        selectedTemplate?.fileIds,
        selectedCompanyId,
        selectedEntityId,
        selectedUnitId,
    ]);

    useEffect(() => {
        async function fetchDashboardMetrics() {
            try {
                dispatch(startLoader());
                const params = {
                    companyId: selectedCompanyId || undefined,
                }
                const res = await api.get("/getDashboardMetrics", {
                    headers: getDefaultHeaders(), params
                });

                const data = res.data;

                if (data) {
                    setSummary([
                        { label: "Files Ingested", value: data.totalFiles },
                        { label: "Data Categories", value: data.totalCategories },
                        { label: "Data Templates", value: data.totalTemplates },
                        { label: "Completion for GRI", value: "31%" }, // Replace with actual if available
                    ]);

                    setTopPerformers(
                        (data.topPerformers || []).map(p => ({
                            name: p.name,
                            files: p.files,
                        }))
                    );
                    dispatch(stopLoader());
                }

            } catch (err) {
                dispatch(stopLoader());
                console.error("Failed to fetch dashboard metrics:", err);
            }
        }

        fetchDashboardMetrics();
    }, [selectedCompanyId]);

    // Select options
    const levelOptions = [
        { label: "Company", value: "company" },
        { label: "Entity", value: "entity" },
        { label: "Operating Unit", value: "unit" },
    ];
    const yearOptions = [
        { label: "FY2025", value: "FY2025" },
        { label: "FY2024", value: "FY2024" },
    ];

    const handleExportClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const exportAsPNG = async () => {
        handleClose();
        const canvas = await html2canvas(chartRef.current);
        const link = document.createElement("a");
        link.download = "chart.png";
        link.href = canvas.toDataURL("image/png");
        link.click();
    };

    const exportAsExcel = () => {
        handleClose();
        const formattedData = chartData.map(d => ({
            Date: new Date(d.date).toLocaleDateString('en-US', {
                month: 'short',
                year: 'numeric',
            }), // e.g., Aug 2025
            Consumption: d.consumption,
        }));

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Chart Data");
        XLSX.writeFile(workbook, "chart-data.xlsx");
    };

    return (
        <div className={styles.fileUploadCont}>
            {/* Summary Cards */}
            <Grid container spacing={3} style={{ marginBottom: 24, cursor: "pointer" }}>
                {summary.map((s, idx) => {
                    const redirectPath = {
                        "Files Ingested": "/data-management?section=2&userID=" + userId,
                        "Data Templates": "/data-management/templates?section=5",
                    }[s.label];

                    const helperText = {
                        "Files Ingested": "Click here to upload files",
                        "Data Templates": "Click here to upload templates",
                    }[s.label];

                    return (


                        <Grid item xs={12} sm={6} md={3} key={idx}>
                            <Card
                                onClick={() => {
                                    if (redirectPath) {
                                        history.push(`${redirectPath}&userID=${userId}`);
                                    }
                                }}
                                style={{
                                    borderLeft: `5px solid ${colorMap[idx % colorMap.length]}`,
                                    boxShadow: "0px 2px 10px rgba(0,0,0,0.05)",
                                    cursor: "pointer",
                                    height: 90,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                }}
                            >
                                <CardContent>
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Box display="flex" alignItems="center">
                                            <Avatar
                                                style={{
                                                    backgroundColor: colorMap[idx % colorMap.length],
                                                    marginRight: 12,
                                                }}
                                            >
                                                {iconMap[s.label] || s.label.charAt(0)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle2" color="textSecondary">
                                                    {s.label}
                                                </Typography>
                                                <Typography variant="h5" style={{ fontWeight: 600 }}>
                                                    {s.value}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Info icon with tooltip */}
                                        {helperText && (
                                            <MuiTooltip title={helperText} arrow>
                                                <IconButton size="small" onClick={(e) => e.stopPropagation()}>
                                                    <InfoOutlinedIcon fontSize="small" style={{ color: "#1976d2" }} />
                                                </IconButton>
                                            </MuiTooltip>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                    );
                })}
            </Grid>

            <Typography variant="subtitle1" style={{ fontWeight: 600, fontSize: '20px' }}>
                Top Performers
            </Typography>

            <Grid container spacing={2} style={{ marginBottom: 22 }}>
                {topPerformers.map((p, idx) => (
                    <Grid item xs={12} sm={6} md={6} key={idx}>
                        <Paper
                            elevation={3}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "16px 20px",
                                borderRadius: 12,
                                backgroundColor: "#f9f9f9",
                            }}
                        >
                            <Avatar
                                style={{
                                    backgroundColor: rankColors[idx] || "#607d8b",
                                    marginRight: 16,
                                }}
                            >
                                {p.name?.charAt(0) || "?"}
                            </Avatar>

                            <Box flexGrow={1}>
                                <Typography variant="subtitle1" style={{ fontWeight: 600, fontSize: '15px' }}>
                                    {p.name || "Unknown"}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Files Uploaded: {p.files}
                                </Typography>
                            </Box>

                            <Chip
                                icon={<EmojiEvents style={{ color: rankColors[idx] || "#607d8b" }} />}
                                label={`#${idx + 1}`}
                                style={{
                                    backgroundColor: "#fff",
                                    border: `1px solid ${rankColors[idx] || "#ccc"}`,
                                    color: rankColors[idx] || "#607d8b",
                                    fontWeight: 500,
                                }}
                            />
                        </Paper>
                    </Grid>
                ))}
            </Grid>
            <hr></hr>

            <br></br>
            {/* Filters */}
            <Box mb={4}>
                <Grid container spacing={2}>
                    {/* Level */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography><b>Select Level</b></Typography>
                        <Select
                            options={levelOptions}
                            value={levelOptions.find(o => o.value === selectedUploadLevel)}
                            onChange={opt => setSelectedUploadLevel(opt.value)}
                        />
                    </Grid>

                    {/* Company */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography><b>Select Company</b></Typography>
                        <Select
                            options={companies.map(c => ({ label: c.name, value: c.company_id }))}
                            value={companies
                                .map(c => ({ label: c.name, value: c.company_id }))
                                .find(o => o.value === selectedCompanyId)}
                            onChange={opt => setSelectedCompanyId(opt.value)}
                        />
                    </Grid>

                    {/* Entity */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography><b>Select Entity</b></Typography>
                        <Select
                            options={entities
                                .filter(e => e.company_id === selectedCompanyId)
                                .map(e => ({ label: e.entity_name, value: e.entity_id }))}
                            value={entities
                                .map(e => ({ label: e.entity_name, value: e.entity_id }))
                                .find(o => o.value === selectedEntityId)}
                            onChange={opt => {
                                setSelectedEntityId(opt.value);
                                setSelectedUnitId("");
                                setSelectedTemplate(null);
                                setChartData([]);
                                setTemplates([]);
                                setUnitLabel("");
                            }}
                        />
                    </Grid>

                    {/* Operating Unit */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography><b>Select Operating Unit</b></Typography>
                        <Select
                            options={operatingUnits
                                .filter(u => u.company_id === selectedCompanyId)
                                .map(u => ({ label: u.name, value: u.unit_id }))}
                            value={
                                selectedUnitId
                                    ? operatingUnits
                                        .map(u => ({ label: u.name, value: u.unit_id }))
                                        .find(o => o.value === selectedUnitId)
                                    : null
                            }
                            onChange={opt => setSelectedUnitId(opt.value)}
                        />
                    </Grid>

                    {/* Templates */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography><b>Data Templates</b></Typography>
                        <Select
                            options={templates}
                            value={selectedTemplate}
                            placeholder="Select Template"
                            onChange={opt => setSelectedTemplate(opt)}
                        />
                    </Grid>

                    {/* Years */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography><b>Reporting Year</b></Typography>
                        <Select
                            options={yearOptions}
                            isMulti
                            value={years}
                            onChange={setYears}
                        />
                    </Grid>

                </Grid>
            </Box>
            <Card style={{ marginBottom: 24 }}>
                <CardContent>
                    {/* Chart Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                            {selectedTemplate ? `${selectedTemplate.label}` : ""}
                        </Typography>
                        <Box>
                            {unitLabel && (
                                <Chip
                                    label={`Unit: ${unitLabel}`}
                                    style={{ background: "#e3f2fd", color: "#1976d2", marginRight: 8 }}
                                />
                            )}
                            {chartData.length > 0 && (
                                <>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<ExportIcon />}
                                        onClick={handleExportClick}
                                    >
                                        Export
                                    </Button>
                                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                                        <MenuItem onClick={exportAsPNG}>Download as PNG</MenuItem>
                                        <MenuItem onClick={exportAsExcel}>Download as Excel</MenuItem>
                                    </Menu>
                                </>
                            )}

                        </Box>
                    </Box>

                    {chartData.length > 0 ? (
                        <div ref={chartRef}>
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart
                                    data={chartData.filter(d => d.consumption > 0)}
                                    margin={{ top: 20, right: 10, left: 10, bottom: 60 }}
                                    barCategoryGap="30%"
                                >
                                    <XAxis
                                        dataKey="date"
                                        type="number"
                                        scale="time"
                                        domain={['dataMin', 'dataMax']}
                                        tickFormatter={tick => new Date(tick).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                                        tick={{ angle: -45, textAnchor: 'end' }}
                                        interval={0}
                                        padding={{ left: 20, right: 0 }}
                                    />
                                    <YAxis
                                        label={{ value: unitLabel, angle: -90, position: "insideLeft", offset: 0 }}
                                    />
                                    <RechartsTooltip
                                        labelFormatter={label =>
                                            new Date(label).toLocaleDateString('en-US', {
                                                month: 'short',
                                                year: '2-digit',
                                            })
                                        }
                                        formatter={val => [val, unitLabel]}
                                    />
                                    <Bar
                                        dataKey="consumption"
                                        fill="#38446c"
                                        barSize={20}
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <Box
                            height={300}
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                            alignItems="center"
                            color="textSecondary"
                        >
                            <img
                                src={EmptyChartImage}
                                alt="No Data"
                                height={100}
                                style={{ marginBottom: 16 }}
                            />
                            <Typography variant="subtitle1">No data available to display.</Typography>
                            <Typography variant="body2">
                                Please select a template or adjust filters to view the chart.
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>


        </div>
    );
};

export default Report;
